import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import { fetchWithAuth } from '../lib/api';

interface CollaboratorsPopoverProps {
  mode: 'outgoing' | 'incoming';
  emails?: string[];
  ownerName?: string;
  children: React.ReactNode;
}

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 150;

export const CollaboratorsPopover: React.FC<CollaboratorsPopoverProps> = ({ mode, emails, ownerName, children }) => {
  const { t } = useTranslation();
  const { isTouch } = useIsMobile();
  const [open, setOpen] = useState(false);
  const [exists, setExists] = useState<Record<string, boolean>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestedRef = useRef<Set<string>>(new Set());

  const clearPendingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleOpen = () => {
    clearPendingTimeout();
    timeoutRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    clearPendingTimeout();
    timeoutRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => clearPendingTimeout, []);

  useEffect(() => {
    if (!isTouch || !open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isTouch, open]);

  // Lazily resolve registered/unregistered status the first time the popover opens,
  // matching the green/red distinction used in ShareDialog and Settings > Contacts.
  useEffect(() => {
    if (mode !== 'outgoing' || !open || !emails || emails.length === 0) return;
    const toFetch = emails.filter((email) => !requestedRef.current.has(email));
    if (toFetch.length === 0) return;
    toFetch.forEach((email) => requestedRef.current.add(email));

    let cancelled = false;
    toFetch.forEach(async (email) => {
      try {
        const res = await fetchWithAuth(`/api/users/check?email=${encodeURIComponent(email)}`);
        if (!cancelled) setExists((prev) => ({ ...prev, [email]: !!res?.exists }));
      } catch {
        if (!cancelled) setExists((prev) => ({ ...prev, [email]: true }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [mode, open, emails]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  const triggerProps = isTouch
    ? {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        },
      }
    : {
        onMouseEnter: scheduleOpen,
        onMouseLeave: scheduleClose,
        onFocus: () => setOpen(true),
        onBlur: scheduleClose,
      };

  const popoverTitle =
    mode === 'outgoing'
      ? t('sharing.collaborators_popover_title', 'Shared with')
      : t('sharing.collaborators_popover_owner_title', 'Owner');

  return (
    <div
      ref={wrapperRef}
      className="collaborators-popover-trigger"
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      {...triggerProps}
    >
      {children}
      {open && (
        <div className="collaborators-popover" onMouseEnter={clearPendingTimeout} onMouseLeave={scheduleClose}>
          <div className="collaborators-popover-header">{popoverTitle}</div>
          {mode === 'outgoing' ? (
            (emails && emails.length > 0) ? (
              <div className="share-pills collaborators-popover-list">
                {emails.map((email) => {
                  const isRegistered = exists[email];
                  const className = isRegistered === undefined ? 'share-pill' : `share-pill ${isRegistered ? 'green' : 'red'}`;
                  return (
                    <div
                      key={email}
                      className={className}
                      title={
                        isRegistered === undefined
                          ? undefined
                          : isRegistered
                          ? t('sharing.registered_user', 'Registered User')
                          : t('sharing.unregistered_user', 'Unregistered User (Will receive email invite)')
                      }
                    >
                      <span>{email}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="collaborators-popover-empty">{t('sharing.no_collaborators', 'No one yet')}</div>
            )
          ) : (
            <div className="share-pills collaborators-popover-list">
              <div className="share-pill green">
                <span>{ownerName || t('sharing.someone', 'Someone')}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
